import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react'
import type { Platform } from '@/types/settings'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { trpc } from '@/lib/trpc'
import type {
    TrackedSKUWithCompetitors,
    CreateTrackedSKUInput,
    CompetitorInfo,
} from '@/types'

// Re-export types for use in other components
export type { TrackedSKUWithCompetitors, CompetitorInfo }

export interface AddSKUFormData {
    sku: string
    name: string
    link: string
    platform: Platform
    brand?: string
    category?: string
    imageUrl?: string
    price?: number
    mrp?: number
    discountPercent?: number
    /** Platform-specific metadata (e.g. Instamart parentProductId) */
    metadata?: Record<string, unknown>
}

interface SettingsContextType {
    // SKU Management
    trackedSKUs: TrackedSKUWithCompetitors[]
    isLoading: boolean
    error: string | null
    workspaceId: string | null
    addTrackedSKU: (data: AddSKUFormData, competitors?: { externalId: string; name: string; brand?: string; imageUrl?: string; link?: string; price?: number; mrp?: number; discountPercent?: number; rating?: number; ratingCount?: number; quantity?: string; unit?: string }[]) => Promise<TrackedSKUWithCompetitors | null>
    removeTrackedSKU: (id: string) => Promise<boolean>
    updateTrackedSKU: (id: string, data: Partial<CreateTrackedSKUInput>) => Promise<TrackedSKUWithCompetitors | null>
    toggleSKUActive: (id: string) => Promise<TrackedSKUWithCompetitors | null>
    getTrackedSKUs: (platform?: Platform) => TrackedSKUWithCompetitors[]
    getActiveSKUs: (platform?: Platform) => TrackedSKUWithCompetitors[]
    // Competitor Management
    addCompetitors: (skuId: string, competitors: { externalId: string; name: string; brand?: string; imageUrl?: string; link?: string; price?: number; mrp?: number; discountPercent?: number; rating?: number; ratingCount?: number; quantity?: string; unit?: string }[]) => Promise<TrackedSKUWithCompetitors | null>
    removeCompetitor: (skuId: string, competitorId: string) => Promise<TrackedSKUWithCompetitors | null>
    // Brand
    userBrand: string
    setUserBrand: (brand: string) => void
    // Refresh data
    refreshSKUs: () => Promise<void>
}

const BRAND_STORAGE_KEY = 'reflow_user_brand'

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

export function SettingsProvider({ children }: { children: ReactNode }) {
    const { currentWorkspace } = useWorkspace()
    const workspaceId = currentWorkspace?.id
    const utils = trpc.useUtils()

    const [error, setError] = useState<string | null>(null)
    const [userBrand, setUserBrandState] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem(BRAND_STORAGE_KEY) || 'Nourish You'
        }
        return 'Nourish You'
    })

    // Fetch SKUs from API when workspace changes
    const skusQuery = trpc.trackedSKU.list.useQuery(
        { workspaceId: workspaceId! },
        { enabled: !!workspaceId }
    )

    const trackedSKUs = useMemo(
        () => (skusQuery.data?.skus ?? []) as unknown as TrackedSKUWithCompetitors[],
        [skusQuery.data]
    )

    // Mutations
    const createMutation = trpc.trackedSKU.create.useMutation({
        onSuccess: () => { utils.trackedSKU.list.invalidate() },
    })

    const updateMutation = trpc.trackedSKU.update.useMutation({
        onSuccess: () => { utils.trackedSKU.list.invalidate() },
    })

    const deleteMutation = trpc.trackedSKU.delete.useMutation({
        onSuccess: () => { utils.trackedSKU.list.invalidate() },
    })

    const toggleMutation = trpc.trackedSKU.toggle.useMutation({
        onSuccess: () => { utils.trackedSKU.list.invalidate() },
    })

    const addCompetitorsMutation = trpc.trackedSKU.addCompetitors.useMutation({
        onSuccess: () => { utils.trackedSKU.list.invalidate() },
    })

    const removeCompetitorMutation = trpc.trackedSKU.removeCompetitor.useMutation({
        onSuccess: () => { utils.trackedSKU.list.invalidate() },
    })

    const addTrackedSKU = useCallback(async (
        data: AddSKUFormData,
        competitors?: { externalId: string; name: string; brand?: string; imageUrl?: string; link?: string; price?: number; mrp?: number; discountPercent?: number }[]
    ) => {
        if (!workspaceId) {
            setError('No workspace selected')
            return null
        }

        setError(null)
        try {
            const result = await createMutation.mutateAsync({
                workspaceId,
                externalId: data.sku,
                name: data.name,
                brand: data.brand,
                category: data.category,
                imageUrl: data.imageUrl,
                link: data.link,
                platform: data.platform,
                price: data.price,
                mrp: data.mrp,
                discountPercent: data.discountPercent,
                metadata: data.metadata,
                competitors,
            })
            return result as unknown as TrackedSKUWithCompetitors
        } catch (err) {
            console.error('Failed to add tracked SKU:', err)
            setError('Failed to add SKU')
            return null
        }
    }, [workspaceId, createMutation])

    const removeTrackedSKU = useCallback(async (id: string) => {
        if (!workspaceId) {
            setError('No workspace selected')
            return false
        }

        setError(null)
        try {
            await deleteMutation.mutateAsync({ workspaceId, skuId: id })
            return true
        } catch (err) {
            console.error('Failed to remove tracked SKU:', err)
            setError('Failed to remove SKU')
            return false
        }
    }, [workspaceId, deleteMutation])

    const updateTrackedSKUFn = useCallback(async (id: string, data: Partial<CreateTrackedSKUInput>) => {
        if (!workspaceId) {
            setError('No workspace selected')
            return null
        }

        setError(null)
        try {
            const result = await updateMutation.mutateAsync({
                workspaceId,
                skuId: id,
                ...data,
            })
            return result as unknown as TrackedSKUWithCompetitors
        } catch (err) {
            console.error('Failed to update tracked SKU:', err)
            setError('Failed to update SKU')
            return null
        }
    }, [workspaceId, updateMutation])

    const toggleSKUActive = useCallback(async (id: string) => {
        if (!workspaceId) {
            setError('No workspace selected')
            return null
        }

        setError(null)
        try {
            const result = await toggleMutation.mutateAsync({ workspaceId, skuId: id })
            return result as unknown as TrackedSKUWithCompetitors
        } catch (err) {
            console.error('Failed to toggle tracked SKU:', err)
            setError('Failed to toggle SKU')
            return null
        }
    }, [workspaceId, toggleMutation])

    const addCompetitors = useCallback(async (
        skuId: string,
        competitors: { externalId: string; name: string; brand?: string; imageUrl?: string; link?: string; price?: number; mrp?: number; discountPercent?: number; rating?: number; ratingCount?: number; quantity?: string; unit?: string }[]
    ) => {
        if (!workspaceId) {
            setError('No workspace selected')
            return null
        }

        setError(null)
        try {
            const result = await addCompetitorsMutation.mutateAsync({
                workspaceId,
                skuId,
                competitors,
            })
            return result as unknown as TrackedSKUWithCompetitors
        } catch (err) {
            console.error('Failed to add competitors:', err)
            setError('Failed to add competitors')
            return null
        }
    }, [workspaceId, addCompetitorsMutation])

    const removeCompetitor = useCallback(async (skuId: string, competitorId: string) => {
        if (!workspaceId) {
            setError('No workspace selected')
            return null
        }

        setError(null)
        try {
            const result = await removeCompetitorMutation.mutateAsync({
                workspaceId,
                skuId,
                competitorId,
            })
            return result as unknown as TrackedSKUWithCompetitors
        } catch (err) {
            console.error('Failed to remove competitor:', err)
            setError('Failed to remove competitor')
            return null
        }
    }, [workspaceId, removeCompetitorMutation])

    const getTrackedSKUsFn = useCallback((platform?: Platform) => {
        if (platform) {
            return trackedSKUs.filter(sku => sku.platform === platform)
        }
        return trackedSKUs
    }, [trackedSKUs])

    const getActiveSKUs = useCallback((platform?: Platform) => {
        return getTrackedSKUsFn(platform).filter(sku => sku.isActive)
    }, [getTrackedSKUsFn])

    const setUserBrand = useCallback((brand: string) => {
        setUserBrandState(brand)
        if (typeof window !== 'undefined') {
            localStorage.setItem(BRAND_STORAGE_KEY, brand)
        }
    }, [])

    const refreshSKUs = useCallback(async () => {
        await utils.trackedSKU.list.invalidate()
    }, [utils])

    const value: SettingsContextType = {
        trackedSKUs,
        isLoading: skusQuery.isLoading,
        error: skusQuery.error?.message ?? error,
        workspaceId: workspaceId || null,
        addTrackedSKU,
        removeTrackedSKU,
        updateTrackedSKU: updateTrackedSKUFn,
        toggleSKUActive,
        getTrackedSKUs: getTrackedSKUsFn,
        getActiveSKUs,
        addCompetitors,
        removeCompetitor,
        userBrand,
        setUserBrand,
        refreshSKUs,
    }

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    )
}

export function useSettings() {
    const context = useContext(SettingsContext)
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider')
    }
    return context
}
